import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

function getSameOriginReferrerPath(): string | null {
  if (typeof window === 'undefined' || !document.referrer) {
    return null;
  }

  try {
    const referrerUrl = new URL(document.referrer);
    if (referrerUrl.origin !== window.location.origin) {
      return null;
    }

    return `${referrerUrl.pathname}${referrerUrl.search}${referrerUrl.hash}`;
  } catch {
    return null;
  }
}

// Tracks last non-auth in-app route so pages can implement a safe "Regresar" without hitting OAuth screens
export default function RouteHistoryTracker() {
  const location = useLocation();
  const didBootstrapHistoryRef = useRef(false);

  useEffect(() => {
    try {
      const path = location.pathname + (location.search || '') + (location.hash || '');

      const isAuth = (p: string) => (
        p.startsWith('/login') || p.startsWith('/register') || p.startsWith('/auth/callback') ||
        p.startsWith('/request-password-reset') || p.startsWith('/update-password')
      );

      if (isAuth(path)) {
        return;
      }

      if (!didBootstrapHistoryRef.current) {
        didBootstrapHistoryRef.current = true;

        const referrerPath = getSameOriginReferrerPath();
        if (referrerPath && referrerPath !== path && !isAuth(referrerPath)) {
          sessionStorage.setItem('rc:lastAppPath', referrerPath);
        } else {
          sessionStorage.removeItem('rc:lastAppPath');
        }

        sessionStorage.setItem('rc:currentAppPath', path);
        return;
      }

      // Read previous current path
      const prev = sessionStorage.getItem('rc:currentAppPath') || '';

      // Update lastAppPath only when moving between two non-auth routes and actually changing path
      if (prev && prev !== path && !isAuth(prev)) {
        sessionStorage.setItem('rc:lastAppPath', prev);
      }

      // Always update currentAppPath for non-auth routes
      sessionStorage.setItem('rc:currentAppPath', path);
    } catch {
      // ignore storage errors (private mode, etc.)
    }
  }, [location]);

  return null;
}
